import { isPrunable, type OutboxEntry } from '@/domain';
import type { EnqueueRequest, OutboxQueue } from '@/application/ports/OutboxQueue';

/**
 * The outbox without a database behind it.
 *
 * Used in two places, and the second is not a test convenience. Some browsers
 * refuse IndexedDB in private mode, and a technician whose phone is in that
 * state must still be able to work: they lose durability across a reload, not
 * the ability to record a completed task. Falling back is better than a screen
 * that throws on first use.
 */
export class MemoryOutboxQueue implements OutboxQueue {
  private readonly entries = new Map<string, OutboxEntry>();
  private readonly listeners = new Set<() => void>();

  private notify(): void {
    for (const listener of this.listeners) listener();
  }

  enqueue(request: EnqueueRequest): Promise<OutboxEntry> {
    const entry: OutboxEntry = { ...request, attempts: 0, lastError: null, status: 'pending' };
    this.entries.set(entry.id, entry);
    this.notify();
    return Promise.resolve(entry);
  }

  all(): Promise<OutboxEntry[]> {
    return Promise.resolve([...this.entries.values()]);
  }

  markSent(id: string): Promise<void> {
    return this.patch(id, (entry) => ({ ...entry, status: 'sent', lastError: null }));
  }

  markFailed(id: string, error: string, retryable: boolean): Promise<void> {
    return this.patch(id, (entry) => ({
      ...entry,
      attempts: entry.attempts + 1,
      lastError: error,
      status: retryable ? 'pending' : 'failed',
    }));
  }

  discard(id: string): Promise<void> {
    this.entries.delete(id);
    this.notify();
    return Promise.resolve();
  }

  prune(now: number): Promise<void> {
    for (const entry of [...this.entries.values()]) {
      if (isPrunable(entry, now)) this.entries.delete(entry.id);
    }
    this.notify();
    return Promise.resolve();
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private patch(id: string, change: (entry: OutboxEntry) => OutboxEntry): Promise<void> {
    const existing = this.entries.get(id);
    if (existing !== undefined) this.entries.set(id, change(existing));
    this.notify();
    return Promise.resolve();
  }
}
